// The client portal's data, as the portal_* functions in Supabase return it, and the small rules the
// screens share. Nothing here decides what anyone may see: Supabase filters every call by the signed-in
// person's scope. The portal never receives spend or staff addresses, and receives a lead's source only for
// admins and the logins allowed to see sources (rnrinfra 20261014160000_portal_kpis_export_sources).
import type {DashboardAccessLevel} from './types';
import {csvCell} from './inventory-export';

export interface PortalWebsite {website_id:number;domain:string|null;phone:string|null}
export interface PortalBusiness {
 business_id:string;agency_id:string;name:string;status:string;alerts_paused:boolean;
 account_manager:{name:string;email:string|null}|null;websites:PortalWebsite[];
}
export interface PortalBootstrap {
 email:string;display_name:string|null;access_level:DashboardAccessLevel;agency_id:string|null;
 is_staff:boolean;staff_pages:boolean;can_view_as:boolean;
 agencies:Array<{agency_id:string;name:string}>;businesses:PortalBusiness[];
}
export interface PortalOverview {
 days:number;leads:number;calls:number;forms:number;previous_leads:number;filtered:number;
 booked:number;won:number;lost:number;revenue:number;with_outcome:number;
 rated?:number;good?:number;okay?:number;bad?:number;
 weekly:Array<{week:string;leads:number}>;by_website:Array<{website:string;leads:number}>;
 by_client:Array<{business_id:string;name:string;leads:number}>;
}
export interface PortalLeadRow {
 lead_id:number;business_id:string;business_name:string;received_at:string;channel:string;
 full_name:string|null;phone:string|null;email:string|null;city:string|null;postal_code:string|null;service:string|null;
 website_id:number|null;website:string|null;filtered:boolean;call_seconds:number|null;answered:boolean|null;
 has_recording:boolean;has_transcript:boolean;
 appointment_status:string|null;appointment_at:string|null;sale_status:string|null;revenue_amount:number|null;
 qualification:string|null;outcome_revision:number;
 lead_quality?:LeadQuality;quality_reasons?:QualityReason[];
 /** Only for admins and source logins. */
 source?:LeadSource;
}
/** sources is there only when the rows carry a source; export and truncated only for a download. */
export interface PortalLeadList {total:number;rows:PortalLeadRow[];sources?:boolean;export?:boolean;truncated?:boolean}

// Where a lead came from, as the hub's source matrix names it.
export type LeadSource='paid_google'|'paid_meta'|'paid_other'|'organic_gbp'|'organic_website'|'not_tracked';
export const SOURCE_LABELS:Record<LeadSource,string>={
 paid_google:'Google Ads',paid_meta:'Meta Ads',paid_other:'Other paid',organic_gbp:'Google Business Profile',organic_website:'Website (organic)',not_tracked:'Not tracked',
};
export const sourceLabel=(source:string|null|undefined)=>source?SOURCE_LABELS[source as LeadSource]??source.replaceAll('_',' '):'';

// KPI tracking (portal_kpis): each client's leads in a month against their target. Managers and staff only.
export type KpiPace='met'|'ahead'|'on_track'|'behind'|'no_goal';
export interface PortalKpiClient {
 business_id:string;name:string;status:string;manager:string|null;
 goal:number|null;leads:number;expected:number|null;projected:number|null;pace:KpiPace;
 portal_leads:number;booked:number;won:number;lost:number;revenue:number;rated:number;good:number;bad:number;with_outcome:number;
 paid?:number;organic?:number;
}
export interface PortalKpis {
 month:string;days_in_month:number;share_of_month:number;sources:boolean;clients:PortalKpiClient[];
 totals:{clients:number;leads:number;goal:number|null;with_goal:number;behind:number;portal_leads:number;booked:number;won:number;lost:number;revenue:number;rated:number;paid?:number;organic?:number};
 by_source?:Partial<Record<LeadSource,number>>;
}
export const PACE:Record<KpiPace,{label:string;tone:'won'|'open'|'muted'|'lost'|'unrated';rank:number}>={
 behind:{label:'Behind',tone:'lost',rank:0},no_goal:{label:'No target',tone:'unrated',rank:1},on_track:{label:'On track',tone:'muted',rank:2},
 ahead:{label:'Ahead',tone:'open',rank:3},met:{label:'Target met',tone:'won',rank:4},
};

/** This month and the eleven before it, in New York, as the first of each month. */
export function kpiMonths(now=new Date()):Array<{value:string;label:string}>{
 const parts=new Intl.DateTimeFormat('en-US',{timeZone:'America/New_York',year:'numeric',month:'numeric'}).formatToParts(now);
 const year=Number(parts.find(p=>p.type==='year')?.value),month=Number(parts.find(p=>p.type==='month')?.value);
 return Array.from({length:12},(_,i)=>{
  const d=new Date(Date.UTC(year,month-1-i,1));
  return {value:d.toISOString().slice(0,10),label:new Intl.DateTimeFormat('en-US',{month:'long',year:'numeric',timeZone:'UTC'}).format(d)};
 });
}

const isoLocal=(value:string|null|undefined)=>{
 if(!value||Number.isNaN(Date.parse(value)))return '';
 const d=new Date(value);d.setMinutes(d.getMinutes()-d.getTimezoneOffset());return d.toISOString().slice(0,16).replace('T',' ');
};
const toCsv=(rows:unknown[][])=>rows.map(r=>r.map(csvCell).join(',')).join('\r\n');

/** The leads list as a spreadsheet: every column the list shows and more, Source only when the rows carry one. */
export function leadsCsv(rows:PortalLeadRow[]):string{
 const withSource=rows.some(r=>r.source!==undefined);
 const header=['Received','Client','Website','Name','Phone','Email','City','ZIP','Service','Type','Call length','Spam',...(withSource?['Source']:[]),
  'Status','Appointment','Score','Score reasons','Job $'];
 return toCsv([header,...rows.map(r=>[isoLocal(r.received_at),r.business_name,r.website??'',r.full_name??'',phone(r.phone),r.email??'',r.city??'',r.postal_code??'',r.service??'',
  r.channel==='call'?(r.answered===false?'Missed call':'Call'):r.channel==='website_form'?'Form':r.channel,duration(r.call_seconds),r.filtered?'Yes':'',
  ...(withSource?[sourceLabel(r.source)]:[]),outcomeOf(r).label,isoLocal(r.appointment_at),qualityOf(r.lead_quality).label,(r.quality_reasons??[]).map(reasonLabel).join('; '),
  r.sale_status==='won'&&r.revenue_amount!=null?r.revenue_amount:''])]);
}

/** The KPI table as a spreadsheet, Paid and Organic only when the report carries them. */
export function kpisCsv(k:PortalKpis):string{
 const header=['Client','Account manager','Leads','Target','Expected by today','Projected','Pace',...(k.sources?['Paid','Organic']:[]),'Portal leads','Booked','Sold','Lost','Job $','Scored'];
 return toCsv([header,...k.clients.map(c=>[c.name,c.manager??'',c.leads,c.goal??'',c.expected??'',c.projected??'',PACE[c.pace]?.label??c.pace,...(k.sources?[c.paid??'',c.organic??'']:[]),
  c.portal_leads,c.booked,c.won,c.lost,c.revenue,c.rated])]);
}

/** Saves text as a CSV file, with a BOM so Excel reads it as UTF-8. */
export function downloadCsv(name:string,csv:string){
 const url=URL.createObjectURL(new Blob(['﻿'+csv],{type:'text/csv;charset=utf-8'}));
 const a=document.createElement('a');a.href=url;a.download=name;document.body.append(a);a.click();a.remove();
 setTimeout(()=>URL.revokeObjectURL(url),1000);
}
export interface PortalDelivery {at:string;channel:'text'|'email';status:'sent'|'failed'|'sending';to:string}
export interface PortalOutcomeEntry {
 revision:number;recorded_at:string;by:string;qualification:string;appointment_status:string;appointment_at:string|null;
 sale_status:string;revenue_amount:number|null;reason:string|null;notes:string|null;
 lead_quality?:LeadQuality;quality_reasons?:QualityReason[];internal?:boolean;
}
export interface PortalLeadDetail extends PortalLeadRow {
 address:string|null;message:string|null;first_time_caller:boolean|null;recording_url:string|null;transcript:string|null;
 deliveries:PortalDelivery[];history:PortalOutcomeEntry[];
}
export interface PortalTextContact {membership_id:string;name:string;phone:string;email:string|null;role:string;since:string;opted_out:boolean;texts_failing?:boolean}
export interface PortalEmailRequest {
 request_id:string;action:'add_email'|'remove_email';email:string;note:string;status:'pending'|'done'|'declined';
 created_at:string;resolved_at:string|null;resolution_note:string|null;
}
export interface PortalAlertContacts {business_id:string;alerts_paused:boolean;text_contacts:PortalTextContact[];emails:string[];requests:PortalEmailRequest[]}
/** Staff queue of email changes asked for in the portal. */
export interface PortalStaffRequest {request_id:string;business_id:string;business:string;action:'add_email'|'remove_email';email:string;note:string;requested_by:string|null;created_at:string}

export type LeadSort='received_at'|'name'|'website'|'service'|'status'|'quality'|'job_amount';
export interface LeadFilters {
 days:number;channel:''|'call'|'website_form';outcome:''|'none'|'booked'|'won'|'lost'|'unqualified';search:string;includeFiltered:boolean;
 quality:''|'unrated'|LeadQuality;sort:LeadSort;direction:'asc'|'desc';
}
export const DEFAULT_FILTERS:LeadFilters={days:30,channel:'',outcome:'',search:'',includeFiltered:false,quality:'',sort:'received_at',direction:'desc'};
/** A column's first click sorts the way people expect: newest, biggest and best first; words A to Z. */
export const FIRST_DIRECTION:Record<LeadSort,'asc'|'desc'>={received_at:'desc',name:'asc',website:'asc',service:'asc',status:'desc',quality:'desc',job_amount:'desc'};

// Lead scores: how good a lead was, from the people who worked it. Bad needs a reason; okay may have one.
export type LeadQuality='unknown'|'good'|'okay'|'bad';
export type QualityReason='wrong_service'|'outside_area'|'too_small'|'price_shopping'|'no_contact'|'spam'|'wrong_number'|'duplicate'|'other';
export const QUALITY_CHOICES:Array<{id:Exclude<LeadQuality,'unknown'>;label:string;hint:string}>=[
 {id:'good',label:'Good',hint:'A real job for us'},{id:'okay',label:'Okay',hint:'Real, but not great'},{id:'bad',label:'Bad',hint:'Not a real opportunity'},
];
export const QUALITY_REASONS:Array<{id:QualityReason;label:string}>=[
 {id:'wrong_service',label:'Wrong service'},{id:'outside_area',label:'Outside our area'},{id:'too_small',label:'Too small or low budget'},
 {id:'price_shopping',label:'Just price shopping'},{id:'no_contact',label:"Couldn't reach them"},{id:'spam',label:'Spam or sales call'},
 {id:'wrong_number',label:'Wrong number'},{id:'duplicate',label:'Duplicate'},{id:'other',label:'Other'},
];
export const reasonLabel=(id:string)=>QUALITY_REASONS.find(r=>r.id===id)?.label??id.replaceAll('_',' ');
/** The score as a chip: its word and its colour. */
export function qualityOf(quality:string|null|undefined):{label:string;tone:'good'|'okay'|'bad'|'unrated'}{
 if(quality==='good')return {label:'Good',tone:'good'};
 if(quality==='okay')return {label:'Okay',tone:'okay'};
 if(quality==='bad')return {label:'Bad',tone:'bad'};
 return {label:'Not scored',tone:'unrated'};
}
export const PERIODS=[7,30,90,365] as const;

/** The request portal_leads takes. */
export function leadsRequest(filters:LeadFilters,business:string|null,page:number,pageSize=50){
 return {
  business_id:business??'',days:filters.days,limit:pageSize,offset:page*pageSize,
  channel:filters.channel,outcome:filters.outcome,search:filters.search.trim().slice(0,100),include_filtered:filters.includeFiltered,
  quality:filters.quality,sort:filters.sort,direction:filters.direction,
 };
}

export type OutcomeTone='open'|'progress'|'booked'|'won'|'lost'|'muted';
/** Where a lead stands, in the client's words. */
export function outcomeOf(row:Pick<PortalLeadRow,'appointment_status'|'sale_status'|'qualification'|'outcome_revision'>):{label:string;tone:OutcomeTone}{
 if(row.sale_status==='won')return {label:'Sold',tone:'won'};
 if(row.sale_status==='lost')return {label:'Lost',tone:'lost'};
 if(row.qualification==='unqualified')return {label:'Not a fit',tone:'muted'};
 if(row.appointment_status==='attended')return {label:'Visited',tone:'booked'};
 if(row.appointment_status==='booked')return {label:'Booked',tone:'booked'};
 if(row.appointment_status==='no_show')return {label:'No-show',tone:'lost'};
 if(row.appointment_status==='canceled')return {label:'Canceled',tone:'lost'};
 return row.outcome_revision?{label:'In progress',tone:'progress'}:{label:'Open',tone:'open'};
}

export function duration(seconds:number|null|undefined):string{
 if(seconds==null||!Number.isFinite(seconds)||seconds<0)return '';
 const s=Math.round(seconds);return `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;
}

/** Call, missed call or form, with the call's length. */
export function channelOf(row:Pick<PortalLeadRow,'channel'|'answered'|'call_seconds'>):string{
 if(row.channel==='website_form')return 'Form';
 if(row.channel==='call'){
  if(row.answered===false)return 'Missed call';
  const d=duration(row.call_seconds);return d?`Call · ${d}`:'Call';
 }
 return row.channel?row.channel.replaceAll('_',' '):'Lead';
}

/** US numbers as (555) 555-0101; anything else as given. */
export function phone(value:string|null|undefined):string{
 if(!value)return '';
 const digits=value.replace(/\D/g,'');
 const ten=digits.length===11&&digits.startsWith('1')?digits.slice(1):digits;
 return ten.length===10?`(${ten.slice(0,3)}) ${ten.slice(3,6)}-${ten.slice(6)}`:value;
}

/** Change against the previous period, or null when there is nothing to compare with. */
export function change(current:number,previous:number):number|null{
 if(!previous)return null;
 return Math.round(((current-previous)/previous)*100);
}

export const money=(v:number|null|undefined)=>new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',maximumFractionDigits:0}).format(Number(v)||0);
export const count=(v:number|null|undefined)=>new Intl.NumberFormat('en-US').format(Number(v)||0);
export function when(value:string|null|undefined):string{
 if(!value||Number.isNaN(Date.parse(value)))return '';
 return new Intl.DateTimeFormat('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}).format(new Date(value));
}
export function day(value:string|null|undefined):string{
 if(!value||Number.isNaN(Date.parse(value)))return '';
 return new Intl.DateTimeFormat('en-US',{month:'short',day:'numeric',timeZone:'UTC'}).format(new Date(value));
}

export interface OutcomeForm {
 appointment_status:'unknown'|'booked'|'attended'|'no_show'|'canceled';
 appointment_at:string; // datetime-local value
 sale_status:'unknown'|'won'|'lost';
 revenue:string;
 qualification:'unknown'|'qualified'|'unqualified';
 lead_quality:LeadQuality;
 quality_reasons:QualityReason[];
 reason:string;
 notes:string;
 /** Kept from the client. Only managers, admins and super admins may set it. */
 internal:boolean;
}
export function outcomeFormFrom(lead:PortalLeadDetail|null):OutcomeForm{
 const latest=lead?.history?.[0];
 const local=(v:string|null|undefined)=>{if(!v||Number.isNaN(Date.parse(v)))return '';const d=new Date(v);d.setMinutes(d.getMinutes()-d.getTimezoneOffset());return d.toISOString().slice(0,16);};
 return {
  appointment_status:(latest?.appointment_status as OutcomeForm['appointment_status'])||'unknown',
  appointment_at:local(latest?.appointment_at),
  sale_status:(latest?.sale_status as OutcomeForm['sale_status'])||'unknown',
  revenue:latest?.revenue_amount!=null?String(latest.revenue_amount):'',
  qualification:(latest?.qualification as OutcomeForm['qualification'])||'unknown',
  lead_quality:lead?.lead_quality??latest?.lead_quality??'unknown',
  quality_reasons:[...(lead?.quality_reasons??latest?.quality_reasons??[])],
  reason:'',notes:'',internal:false,
 };
}

/** The first problem with an outcome, in words, or null when it can be saved. Mirrors the database rules. */
export function outcomeProblem(form:OutcomeForm,now=Date.now()):string|null{
 if(form.appointment_status!=='unknown'&&!form.appointment_at)return 'Add the appointment date and time.';
 const at=form.appointment_at?Date.parse(form.appointment_at):NaN;
 if(['attended','no_show'].includes(form.appointment_status)&&at>now+5*60e3)return 'A visit that already happened cannot be in the future.';
 if(form.revenue.trim()){
  if(form.sale_status!=='won')return 'A job amount goes with a sale. Mark the lead sold first.';
  const value=Number(form.revenue.replace(/[$,\s]/g,''));
  if(!Number.isFinite(value)||value<0)return 'Enter the job amount in dollars.';
 }
 if(form.lead_quality==='bad'&&!form.quality_reasons.length&&!form.reason.trim())return 'Choose why this lead was bad.';
 if(form.lead_quality==='unknown'&&form.qualification==='unqualified'&&!form.reason.trim())return 'Say why this lead is not a fit.';
 if(form.reason.length>500)return 'Keep the reason under 500 characters.';
 if(form.notes.length>2000)return 'Keep notes under 2,000 characters.';
 return null;
}

/** The request portal_record_outcome takes. The request ID makes a retried save safe. */
export function outcomeRequest(lead:Pick<PortalLeadDetail,'lead_id'|'outcome_revision'>,form:OutcomeForm,requestId:string){
 const revenue=form.revenue.trim()?Number(form.revenue.replace(/[$,\s]/g,'')):null;
 return {
  lead_id:lead.lead_id,request_id:requestId,expected_revision:lead.outcome_revision,
  appointment_status:form.appointment_status,
  appointment_at:form.appointment_status!=='unknown'&&form.appointment_at?new Date(form.appointment_at).toISOString():null,
  sale_status:form.sale_status,revenue_amount:form.sale_status==='won'?revenue:null,
  qualification:form.qualification,reason:form.reason.trim(),notes:form.notes.trim(),
  lead_quality:form.lead_quality,quality_reasons:['okay','bad'].includes(form.lead_quality)?form.quality_reasons:[],
  ...(form.internal?{internal:true}:{}),
 };
}

/** The request portal_rate_lead takes: one tap on the list scores the lead and keeps the rest of its outcome. */
export function rateRequest(lead:Pick<PortalLeadRow,'lead_id'|'outcome_revision'>,quality:Exclude<LeadQuality,'unknown'>,requestId:string,reasons:QualityReason[]=[]){
 return {lead_id:lead.lead_id,request_id:requestId,expected_revision:lead.outcome_revision,lead_quality:quality,quality_reasons:quality==='good'?[]:reasons};
}

/** A mobile number as the database will store it, or null when it is not a usable US or E.164 number. */
export function e164(value:string):string|null{
 const trimmed=value.trim();const digits=trimmed.replace(/\D/g,'');
 if(trimmed.startsWith('+'))return digits.length>=8&&digits.length<=15?`+${digits}`:null;
 if(digits.length===10)return `+1${digits}`;
 if(digits.length===11&&digits.startsWith('1'))return `+${digits}`;
 return null;
}
export const looksLikeEmail=(value:string)=>/^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(value.trim());

/** Friendly words for the database's refusals, so a client never sees a raw error. */
export function portalError(error:unknown):string{
 const message=String((error as {message?:string})?.message??error??'');
 const known:Array<[RegExp,string]>=[
  [/not one of your clients|lead not found/i,'This lead is not in your account.'],
  [/choose a client/i,'Choose a client first.'],
  [/changed\. Refresh/i,'Someone updated this lead a moment ago. It has been refreshed; check it and save again.'],
  [/tracking lines/i,'That number is one of our tracking lines. Enter a mobile number.'],
  [/valid mobile/i,'Enter a valid mobile number.'],
  [/already receives lead texts/i,'That number already receives lead texts.'],
  [/at most 10 contacts/i,'A client can have up to 10 contacts. Remove one first.'],
  [/keep at least one/i,'Keep at least one number that receives lead texts.'],
  [/valid email/i,'Enter a valid email address.'],
  [/authentication required/i,'Your session ended. Sign in again.'],
  [/membership required|staff access required/i,'Your access has changed. Sign in again or ask your account manager.'],
  [/why this lead was bad/i,'Choose why this lead was bad.'],
  [/keep a note internal/i,'Only your account team can keep a note internal.'],
 ];
 for(const [pattern,text] of known)if(pattern.test(message))return text;
 return message||'Something went wrong. Try again.';
}
